import { emit } from '../events';

/**
 * The AudioContext, its unlock, and its watchdog.
 *
 * Phase 0 proved the important half: after one real tap unlocks the context (107 ms), Web Audio
 * playback fires correctly from `setTimeout` and `setInterval`. That is what lets dwell and
 * scanning speak in Phase 3, and it is why every provider routes through Web Audio rather than
 * `speechSynthesis`.
 *
 * What Phase 0 did **not** establish is what re-locks it. The context never left `running`
 * during the spike, so there is no evidence it re-locks and no evidence it doesn't. The design
 * doc lists backgrounding, screen lock, Siri and calls as suspects. This module therefore does
 * not code against that list at all: it recovers from **any** non-`running` state, on every
 * playback, and logs every transition it sees so the real triggers come from the field rather
 * than from a guess.
 *
 * If recovery fails, the person must be told — a silently dead AudioContext is this app's worst
 * failure mode, because it looks exactly like a working app.
 */

export type AudioState = AudioContextState | 'unavailable';

export interface AudioTransition {
  from: AudioState;
  to: AudioState;
  /** Page visibility at the moment of the transition — our best clue to the cause. */
  visibility: DocumentVisibilityState | 'unknown';
  /** ms since page load, so a sequence can be reconstructed without a wall clock. */
  atMs: number;
  /**
   * Why this entry is here, when the state alone does not say.
   *
   * `recreated` is the one that matters: replacing a suspended context with a fresh suspended one is
   * a `suspended → suspended` transition, which the deduplication below would otherwise drop — and
   * the trail is the only evidence the device session has (C80).
   */
  note?: 'recreated' | 'closed-observed';
}

type Listener = (transition: AudioTransition) => void;

let context: AudioContext | null = null;
let unlocked = false;
let lastState: AudioState = 'unavailable';
const transitions: AudioTransition[] = [];
const listeners = new Set<Listener>();

/** Bounded: this is a diagnostic trail, not a log file. */
const MAX_TRANSITIONS = 100;

function now(): number {
  return typeof performance === 'undefined' ? 0 : Math.round(performance.now());
}

function visibility(): DocumentVisibilityState | 'unknown' {
  return typeof document === 'undefined' ? 'unknown' : document.visibilityState;
}

/**
 * `note` also forces the entry past the same-state check.
 *
 * Without that, the single most important event in this module is invisible: replacing a dead context
 * usually goes `suspended → suspended`, and a trail that drops it says nothing happened at exactly the
 * moment everything did.
 */
function record(to: AudioState, note?: AudioTransition['note']): void {
  if (to === lastState && note === undefined) return;
  const transition: AudioTransition = {
    from: lastState,
    to,
    visibility: visibility(),
    atMs: now(),
    ...(note === undefined ? {} : { note }),
  };
  lastState = to;
  transitions.push(transition);
  if (transitions.length > MAX_TRANSITIONS) transitions.shift();

  // Metadata only — no utterance text ever reaches this path (NFR7).
  console.info(
    '[audio] state',
    transition.from,
    '→',
    transition.to,
    `(${transition.visibility})`,
    note ?? '',
  );
  for (const listener of listeners) {
    try {
      listener(transition);
    } catch (err) {
      console.error('[audio] transition listener failed', err);
    }
  }
}

/** The trail of observed transitions, for Betreuer-Modus diagnostics and the device session. */
export function audioTransitions(): readonly AudioTransition[] {
  return transitions;
}

export function onAudioTransition(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Build a context and arm its watchdog.
 *
 * **The listener closes over its own instance.** It used to read the module variable, which was
 * correct only because there had never been a second context — and the moment there is, a context we
 * have already replaced reports the *live* one's state as its own, resumes it, and can emit
 * `AudioContextLost` about a context nobody is using. That is a worse bug than the one `SPK-18` fixes,
 * and it is one line.
 */
function buildContext(): AudioContext | null {
  try {
    const Ctor =
      window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) {
      record('unavailable');
      return null;
    }
    const created = new Ctor();
    created.addEventListener('statechange', () => {
      // A context we have replaced has no opinions about the one that replaced it.
      if (created !== context) return;
      const state = created.state;
      record(state);
      if (state !== 'running') {
        // Do not wait for the next utterance to discover this. Try immediately, and let
        // ensureRunning try again at playback time if this attempt loses.
        void created.resume().catch(() => {
          emit({ type: 'AudioContextLost' });
        });
      }
    });
    return created;
  } catch (err) {
    console.error('[audio] could not create AudioContext', err);
    record('unavailable');
    return null;
  }
}

export function getAudioContext(): AudioContext | null {
  if (context) return context;
  const created = buildContext();
  if (!created) return null;
  context = created;
  record(created.state);
  return context;
}

/**
 * How many contexts one page load may build, and how close together.
 *
 * **Safari limits how many `AudioContext`s a page may hold** — historically four. So an unbounded
 * "just make a new one" would spend that budget on a morning of repairs and turn a recoverable state
 * into an unrecoverable one. The bound is also what makes the reload in `SHL-09` a last resort rather
 * than a magic button: when this refuses, there is genuinely nothing left in-page to try.
 *
 * The real ceiling on iPadOS 26 is unmeasured — `docs/device-checklist-phase-6.md` asks for it.
 */
const MAX_RECREATIONS_PER_PAGE = 3;
const MIN_MS_BETWEEN_RECREATIONS = 2000;

let recreations = 0;
let lastRecreationAtMs = Number.NEGATIVE_INFINITY;

/**
 * Whether another replacement is permitted. `SHL-09`'s last resort is offered when this is false.
 *
 * **The interval bounds the automatic path only.** `ensureRunning()` runs before every playback, so
 * without it a dead context becomes a replacement per utterance. A person tapping the repair control
 * twice is the opposite case: the second tap has to do something, or the control is lying. Only the
 * count is a real limit — it is Safari's — and `force` skips the interval, never the count.
 */
export function canRecreateAudioContext(force = false): boolean {
  if (recreations >= MAX_RECREATIONS_PER_PAGE) return false;
  return force || now() - lastRecreationAtMs >= MIN_MS_BETWEEN_RECREATIONS;
}

/**
 * Discard the context and build a fresh one (`SPK-18`).
 *
 * Construction outside a gesture is legal and yields a **suspended** context; what needs a gesture is
 * reaching `running`. So this may be called from a resume handler to get rid of a dead object, and
 * only a tap can finish the job.
 *
 * Returns `null` when the bound refuses or construction fails.
 */
export function recreateAudioContext({ force = false } = {}): AudioContext | null {
  if (!canRecreateAudioContext(force)) {
    console.warn('[audio] refusing to replace the context again', { recreations, force });
    return null;
  }

  const outgoing = context;
  // Detached first, so the outgoing watchdog goes quiet before anything else happens.
  context = null;

  try {
    // Closing an already-closed context fails, and that failure must not abort the repair — the whole
    // point is to be rid of it. Both shapes are handled deliberately: `close()` returns a promise that
    // *rejects*, which a bare `void` would leave unhandled, and an implementation may also throw
    // synchronously. A caught sync throw with an unhandled rejection beside it is the version of this
    // that looks correct and fills the console.
    void outgoing?.close().catch(() => {
      // Already gone, which is the state we wanted.
    });
  } catch {
    // Same outcome, arriving synchronously.
  }

  const created = buildContext();
  if (!created) return null;

  context = created;
  unlocked = false;
  recreations += 1;
  lastRecreationAtMs = now();
  // Forced, because a replacement is usually suspended → suspended and the trail is the evidence.
  record(created.state, 'recreated');
  return created;
}

export function audioState(): AudioState {
  return context?.state ?? lastState;
}

/**
 * Read the live state.
 *
 * Through a function on purpose: TypeScript narrows `ctx.state` and keeps that narrowing across an
 * `await`, which is precisely wrong here — the state changing underneath us while we wait is the
 * entire subject of this module. A direct `ctx.state === 'running'` after `await ctx.resume()`
 * fails to compile *because* the compiler believes something that is not true.
 */
const stateOf = (ctx: AudioContext): AudioContextState => ctx.state;

export function isUnlocked(): boolean {
  return unlocked && context?.state === 'running';
}

/**
 * Unlock inside a real user gesture. Playing one silent sample is the established way to get
 * iOS to hand over a running context; `resume()` alone is not reliably enough.
 *
 * **The sample is started before the first `await`, and the order is load-bearing (`SPK-19`).** The
 * silent `start(0)` is the part iOS accepts as an unlock, and an `await` ahead of it spends the
 * gesture — so a repair triggered from a tap would arrive at the one operation that needs a gesture
 * having just given it away. That is C71's failure mode reappearing inside the control built to fix
 * it, which is why the ordering is written down rather than left to look arbitrary.
 */
export async function unlockAudio(): Promise<boolean> {
  const ctx = getAudioContext();
  if (!ctx) return false;

  try {
    const buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(0);

    if (stateOf(ctx) !== 'running') await ctx.resume();

    unlocked = stateOf(ctx) === 'running';
    return unlocked;
  } catch (err) {
    console.error('[audio] unlock failed', err);
    return false;
  }
}

/**
 * Make the context usable, whatever state it is in. Called before **every** playback rather
 * than once at startup, because we do not know when it re-locks.
 *
 * Returns false when the context cannot be revived, which is the caller's cue to show
 * `Ton aktivieren – antippen` — the only honest response is to ask for a tap.
 */
export async function ensureRunning(): Promise<boolean> {
  const ctx = getAudioContext();
  if (!ctx) return false;
  if (stateOf(ctx) === 'running') return true;

  /**
   * Three outcomes, not two (`SPK-18`).
   *
   * A `closed` context rejects every `resume()` it will ever be given, so asking again is not a
   * recovery — it is the loop that made the app silent until somebody relaunched it (C80). The same
   * is true of a `suspended` one whose resume did not take: whatever the reason, handing the same
   * object back for the rest of the page is a guarantee of silence.
   *
   * A replacement built here is `suspended`, because promotion needs a gesture. So this can hand back
   * `false` having *improved* things — the dead object is gone and the next tap has something that can
   * work — and `false` still means "ask for a tap", which is the honest answer either way.
   */
  if (stateOf(ctx) !== 'closed') {
    try {
      await ctx.resume();
    } catch (err) {
      console.warn('[audio] resume threw', err);
    }

    if (stateOf(ctx) === 'running') return true;
  }

  const replacement = recreateAudioContext();
  if (replacement) {
    try {
      await replacement.resume();
    } catch (err) {
      console.warn('[audio] resume threw on the replacement', err);
    }

    if (stateOf(replacement) === 'running') return true;
  }

  emit({ type: 'AudioContextLost' });
  return false;
}

/**
 * Attach the unlock to a real gesture anywhere in the app, and **keep listening**.
 *
 * `pointerdown` and `touchend` both, because a tap that begins outside a target still counts as
 * a gesture, and the person's first touch may not be a clean one.
 *
 * ── Why this does not detach after the first success (C71) ──
 *
 * It used to. `unlockAudio()` succeeded once, the listeners came off for the lifetime of the page, and that
 * was the end of the only gesture-driven recovery in the app. Then iOS suspended the context — a lock screen,
 * a phone call, switching apps — and nothing could ever unlock it again: `resume()` outside a gesture does not
 * work on Safari, so the person tapped `Sprechen`, every provider that needs our context reported itself
 * unavailable, and the app was silent until somebody relaunched it. A tester reported it as a freeze, and it
 * was one.
 *
 * The listeners are `passive` and the handler exits immediately once the context is running, so staying
 * attached costs a state read per touch. That is the whole price of not going mute.
 */
export function unlockOnFirstGesture(root: HTMLElement | Document = document): () => void {
  const events: Array<keyof HTMLElementEventMap> = ['pointerdown', 'touchend', 'keydown'];

  const handler = (): void => {
    if (isUnlocked()) return;
    void unlockAudio();
  };

  const detach = (): void => {
    for (const type of events) root.removeEventListener(type, handler);
  };

  for (const type of events) root.addEventListener(type, handler, { passive: true });
  return detach;
}

/** Test seam. */
export function resetAudioForTests(): void {
  context = null;
  unlocked = false;
  lastState = 'unavailable';
  transitions.length = 0;
  listeners.clear();
  recreations = 0;
  lastRecreationAtMs = Number.NEGATIVE_INFINITY;
}
